import { useIntl } from 'react-intl'
import { NavLink } from 'react-router'

import { useAuth } from '@/shared/auth/auth-context'
import { sanitizeReturnPath } from '@/shared/auth/return-path'
import { useLinkAccess, useRegistry } from '@/shared/registry'
import { cn, focusRing } from '@/shared/ui/cn'

import { AdminMenu } from './AdminMenu'
import type { SiteNavigationLink } from './site-branding'

// At least 24 px tall, so each link meets 2.5.8 on its own, without the spacing exception.
const navLinkClass = 'flex min-h-6 items-center gap-1 rounded text-sm transition'

function navItemClass({ isActive }: { readonly isActive: boolean }) {
  return cn(
    navLinkClass,
    focusRing,
    // Only an inactive link fades on hover: a faded current link would fall below 4.5:1.
    isActive ? 'font-semibold text-primary-main' : 'font-medium text-text-primary hover:opacity-70',
  )
}

/** Whether a site link's target is a path in this app. */
function isRouterPath(target: string): boolean {
  return sanitizeReturnPath(target) === target
}

/** Whether a site link's target is an `https` or `http` URL, which is the only other kind the API takes. */
function isWebUrl(target: string): boolean {
  try {
    const url = new URL(target)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * The nav in the header bar: the Admin menu and every feature's nav items for a signed-in
 * viewer, then the site's own links. The mockups' Contact, Help and tour links wait for real
 * destinations. On a phone it takes its own row; at any width it wraps, so a site's 20 links never
 * push the account controls off screen. With nothing to show it renders no empty landmark.
 */
export function AppNav(props: { readonly links?: readonly SiteNavigationLink[] }) {
  const intl = useIntl()
  const { user } = useAuth()
  const { nav } = useRegistry()
  const canSee = useLinkAccess()
  // The API checked each target; a path goes through the router, anything else must be a web URL.
  const siteLinks = (props.links ?? []).filter(
    (link) =>
      (user !== null || !link.signedInOnly) &&
      (isRouterPath(link.target) || isWebUrl(link.target)),
  )

  if (user === null && siteLinks.length === 0) {
    return null
  }

  return (
    <nav
      aria-label={intl.formatMessage({ id: 'shell.nav.label' })}
      className="relative order-last flex w-full flex-wrap items-center gap-x-6 gap-y-2 py-1 md:order-none md:w-auto md:min-w-0 md:flex-1 md:py-2"
    >
      {user !== null && (
        <>
          <AdminMenu />
          {nav.filter((item) => canSee(item)).map((item) => (
            <NavLink key={item.id} to={item.path} className={navItemClass}>
              {intl.formatMessage({ id: item.labelId })}
            </NavLink>
          ))}
        </>
      )}
      {siteLinks.map((link, index) =>
        isRouterPath(link.target) ? (
          <NavLink key={`${String(index)}:${link.target}`} to={link.target} className={navItemClass}>
            {link.label}
          </NavLink>
        ) : (
          <a key={`${String(index)}:${link.target}`} href={link.target} className={navItemClass({ isActive: false })}>
            {link.label}
          </a>
        ),
      )}
    </nav>
  )
}
